/**
 * Custom hook for managing user's saved payment data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchUserPaymentData, saveUserPaymentData, updateUserPaymentData } from '@/services/api'
import type { UserPaymentData } from '@/types'

/**
 * Hook to fetch user's saved payment data by device tag
 * @param deviceTag - The device tag identifier
 * @param enabled - Whether the query should run
 * @returns Query result with user payment data
 */
export function useUserPaymentData(deviceTag: string, enabled: boolean = true) {
  return useQuery<UserPaymentData | null, Error>({
    queryKey: ['userPaymentData', deviceTag],
    queryFn: () => fetchUserPaymentData(deviceTag),
    enabled: enabled && !!deviceTag,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
  })
}

/**
 * Hook to save user's payment data (creates new user)
 * @returns Mutation function to save payment data
 */
export function useSaveUserPaymentData() {
  const queryClient = useQueryClient()

  return useMutation<
    UserPaymentData,
    Error,
    { tag: string; mobile_money: { operator: string; mobile_number: string } }
  >({
    mutationFn: ({ tag, mobile_money }) => saveUserPaymentData({ tag, mobile_money }),
    onSuccess: (data) => {
      // Invalidate and refetch user payment data
      queryClient.invalidateQueries({ queryKey: ['userPaymentData', data.tag] })
    },
  })
}

/**
 * Hook to update user's payment data by adding a new mobile money account (PATCH)
 * @returns Mutation function to update payment data
 */
export function useUpdateUserPaymentData() {
  const queryClient = useQueryClient()

  return useMutation<
    UserPaymentData,
    Error,
    { userId: number; tag: string; mobile_money: { operator: string; mobile_number: string } }
  >({
    mutationFn: ({ userId, tag, mobile_money }) => updateUserPaymentData(userId, { tag, mobile_money }),
    onSuccess: (data) => {
      // Invalidate and refetch user payment data
      queryClient.invalidateQueries({ queryKey: ['userPaymentData', data.tag] })
    },
  })
}


